import { useCallback, useEffect, useMemo, useState } from 'react';

import moment from 'moment';

import { CalendarAdornmentProps } from '../CalendarAdornment.props';

type UseDatePickerProps = Pick<CalendarAdornmentProps, 'value' | 'type' | 'dateFormat' | 'disabled' | 'onChange'>;

export const useDatePicker = ({ value, type, dateFormat, disabled, onChange }: UseDatePickerProps) => {
  const [currentDate, setCurrentDate] = useState<Date>(moment().toDate());

  const dateValue = useMemo(() => {
    const currentDateMoment = moment(currentDate, dateFormat);

    if (!currentDateMoment.isValid()) {
      return moment();
    }

    // Checking if the year is entered completely based on dateFormat
    if (value && typeof value === 'string' && dateFormat) {
      // Determine the expected length of the year from the format (YYYY = 4, YY = 2)
      const yearFormatMatch = dateFormat.match(/Y+/);

      if (yearFormatMatch) {
        const expectedYearLength = yearFormatMatch[0].length; // 2 для YY, 4 для YYYY
        const yearStartIndex = dateFormat.indexOf(yearFormatMatch[0]);

        // Extract the entered part of the year from value
        const yearInValue = value.substring(yearStartIndex, yearStartIndex + expectedYearLength);

        // Check that year contains only numbers and its length is less than expected.
        const isPartialYear = /^\d+$/.test(yearInValue) && yearInValue.length < expectedYearLength;

        if (isPartialYear) {
          // The year is not entered completely - we use the month and day from the input, but the current year
          const currentYear = moment().year();
          return moment(currentDateMoment).year(currentYear);
        }
      }
    }

    return currentDateMoment;
  }, [currentDate, dateFormat, value]);

  const handleDateChange = useCallback(
    (date?: Date, callback?: () => void) => {
      if (!date || !dateFormat) {
        return;
      }

      if (onChange) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange({ target: { value: moment(date).format(dateFormat) } });
      }

      setCurrentDate(date);

      callback?.();
    },
    [dateFormat, onChange],
  );

  useEffect(() => {
    if (type !== 'date') {
      return;
    }

    if (value && dateFormat) {
      setCurrentDate(moment(value as number, dateFormat).toDate());
    }
  }, [dateFormat, type, value, disabled]);

  return {
    dateValue,
    handleDateChange,
  };
};
